/*
 * mg-exp-runtime.js — Replica Handbag in-theme replacement for the Moonshot AI (msai) experiment runner.
 *
 * Why this exists: the client is pausing Moonshot AI but wants to keep the winning/deployed
 * experiments live. Each experiment's variant code (assets/mg-exp-*.js) is the exact code that
 * Moonshot served from cdn.moonshot-ai.com/snippets/36780565.js, with the call to
 * `window.msaiTools._msaiRunVariantBlock` redirected to `MGExp.runVariantBlock` below, which
 * re-implements that helper's behaviour (wait for selectors, inject CSS once, re-apply when the
 * DOM re-renders, tear down when the page identifier disappears).
 *
 * Targeting (which page / device) lives in MGExp.targets and mirrors Moonshot's audience+target
 * rules 1:1 (see MOONSHOT_MIGRATION.md in the repo root).
 *
 * Enable/disable per experiment: Theme settings > "Moonshot Migration" (snippets/mg-experiments.liquid).
 *
 * Debug / QA helpers (no theme save needed):
 *   ?mgexp=off   -> disables every mg-exp script for the rest of this browser tab session
 *   ?mgexp=on    -> re-enables
 *   ?mgexp_debug=1 -> console logging of what ran, what was skipped and why (also sticky per tab)
 *   window.MGExp.status() -> table of every registered experiment and its state
 */
(function () {
  'use strict';
  if (window.MGExp) return;

  var sessionGet = function (k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } };
  var sessionSet = function (k, v) { try { v === null ? sessionStorage.removeItem(k) : sessionStorage.setItem(k, v); } catch (e) {} };

  // --- URL toggles -------------------------------------------------------------------------
  try {
    var qs = new URLSearchParams(location.search);
    var toggle = qs.get('mgexp');
    if (toggle !== null) sessionSet('mgexp_off', /^(off|0|false|no)$/i.test(toggle) ? '1' : null);
    var dbg = qs.get('mgexp_debug');
    if (dbg !== null) sessionSet('mgexp_debug', /^(0|false|no|off)$/i.test(dbg) ? null : '1');
  } catch (e) {}

  var DISABLED = sessionGet('mgexp_off') === '1';
  var DEBUG = sessionGet('mgexp_debug') === '1' || !!window.mgexp_debug;
  var log = function () {
    if (!DEBUG || !window.console) return;
    try { console.log.apply(console, ['[MGExp]'].concat([].slice.call(arguments))); } catch (e) {}
  };
  var warn = function () {
    if (!window.console) return;
    try { console.warn.apply(console, ['[MGExp]'].concat([].slice.call(arguments))); } catch (e) {}
  };

  // --- Platform: identical logic to Moonshot's getPlatform() --------------------------------
  function getPlatform() {
    if (navigator.userAgentData) return navigator.userAgentData.mobile ? 'mobile' : 'desktop';
    return /(Mobi|Android|iP(hone|od|ad)|IEMobile|BlackBerry|Opera Mini)/.test(navigator.userAgent) ? 'mobile' : 'desktop';
  }

  // --- Targets: mirror of Moonshot "targets" (URL patterns + JS rule) -----------------------
  // URL patterns are evaluated in order, last match wins (INCLUDE => true, EXCLUDE => false).
  function pdpPrice() {
    var el = document.querySelector('#price');
    if (!el) return null;
    var v = parseFloat(el.innerText.replace(/[^\d.-]/g, ''));
    return isNaN(v) ? null : v;
  }
  function priceRule(test) {
    return function () { var p = pdpPrice(); return p !== null && p > 0 && test(p); };
  }
  var TRUE = function () { return true; };
  var targets = {
    // id: [ [ [inclusion, matchType, pattern], ... ], jsRule, humanName ]
    side_cart:            [[['I', 'REGEX', '.*']], function () { return !!document.querySelector('.bag-container'); }, 'Side Cart (.bag-container present)'],
    pdp:                  [[['I', 'SUBSTRING', '/products/']], TRUE, 'PDP'],
    pdp_0_1500:           [[['I', 'SUBSTRING', '/products/']], priceRule(function (p) { return p < 1500; }), 'PDP price < 1,500'],
    pdp_1500_10000:       [[['I', 'SUBSTRING', '/products/']], priceRule(function (p) { return p > 1500 && p <= 10000; }), 'PDP price 1,500.01 - 10,000'],
    pdp_10000_17500:      [[['I', 'SUBSTRING', '/products/']], priceRule(function (p) { return p > 10000 && p <= 17500; }), 'PDP price 10,000.01 - 17,500'],
    pdp_gt_17500:         [[['I', 'SUBSTRING', '/products/']], priceRule(function (p) { return p > 17500; }), 'PDP price > 17,500'],
    pdp_gt_4000:          [[['I', 'SUBSTRING', '/products/']], priceRule(function (p) { return p > 4000; }), 'PDP price > 4,000'],
    plp:                  [[['I', 'SUBSTRING', '/collectsions/'], ['E', 'SUBSTRING', '/products/']], TRUE, 'PLP'],
    plp_except_hermes:    [[
                             ['I', 'SUBSTRING', '/collectsions/'],
                             ['E', 'SUBSTRING', '/products/'],
                             ['E', 'SUBSTRING', '/collectsions/hermes'],
                             ['E', 'SUBSTRING', '/collectsions/hermes-over-10-000'],
                             ['E', 'SUBSTRING', '/collectsions/hermes-constance-bags'],
                             ['E', 'SUBSTRING', '/collectsions/mini-kelly-bags'],
                             ['I', 'SUBSTRING', '/collectsions/hermes-kelly-bags'],
                             ['I', 'SUBSTRING', '/collectsions/hermes-birkin-bags'],
                             ['I', 'SUBSTRING', '/collectsions/hermes-exotic-bags']
                           ], TRUE, 'PLP except Hermès (Kelly/Birkin/Exotic allowed)'],
    homepage:             [[['I', 'SIMPLE', 'mygemma.com/']], TRUE, 'Homepage'],
    blog:                 [[['I', 'REGEX', '/blogs/news/.+']], TRUE, 'Blog article']
  };

  function urlMatches(patterns) {
    var href = location.href, path = location.pathname;
    var result = false;
    for (var i = 0; i < patterns.length; i++) {
      var inc = patterns[i][0] === 'I', type = patterns[i][1], pat = patterns[i][2], hit = false;
      if (type === 'SUBSTRING') hit = href.indexOf(pat) !== -1;
      else if (type === 'REGEX') { try { hit = new RegExp(pat).test(href); } catch (e) { hit = false; } }
      else if (type === 'SIMPLE') {
        // Moonshot "simple match": host + path, ignoring protocol, www, query, hash, trailing slash.
        var norm = function (u) { return u.replace(/^https?:\/\//, '').replace(/^www\./, '').split(/[?#]/)[0].replace(/\/+$/, ''); };
        hit = norm(location.host + path) === norm(pat);
      }
      if (hit) result = inc;
    }
    return result;
  }

  // --- CSS helpers ---------------------------------------------------------------------------
  function hash(s) {
    var h = 0;
    for (var i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
    return (h >>> 0).toString(36);
  }
  function injectCSS(css, id) {
    if (!css || document.getElementById(id)) return;
    var st = document.createElement('style');
    st.id = id;
    st.textContent = css;
    (document.head || document.documentElement).appendChild(st);
  }
  function removeCSS(id) {
    var st = document.getElementById(id);
    if (st && st.parentNode) st.parentNode.removeChild(st);
  }

  // --- Element lookup: string selector, or {text: "..."} (Moonshot text selector) -------------
  function findByText(text, exact) {
    if (!document.body) return null;
    try {
      var xp = exact
        ? "//*[not(self::script)][not(self::style)][text()[normalize-space(.)='" + text + "']]"
        : "//*[not(self::script)][not(self::style)][text()[contains(normalize-space(.),'" + text + "')]]";
      return document.evaluate(xp, document.body, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    } catch (e) {
      var it = document.createNodeIterator(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          return !n.textContent.trim() || ['SCRIPT', 'STYLE'].indexOf(n.parentElement.tagName) !== -1 ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        }
      });
      var n;
      while ((n = it.nextNode())) {
        var t = n.textContent.trim();
        if (exact ? t === text : t.indexOf(text) !== -1) return n.parentElement;
      }
      return null;
    }
  }
  function findElement(sel) {
    if (typeof sel === 'string') return document.querySelector(sel);
    if (sel && typeof sel === 'object' && sel.text) return findByText(sel.text, !!sel.exact);
    return null;
  }

  // --- runVariantBlock: drop-in for window.msaiTools._msaiRunVariantBlock -------------------
  // (selectors, insertedFlagSelector, callback, css, cleanupFn, config, multiSelectors)
  var registry = [];

  function runVariantBlock(selectors, flagSelector, callback, css, cleanupFn, config, multiSelectors) {
    config = config || {};
    // NOTE: config.multi_target_selectors is only a CSS-validation whitelist in Moonshot, not a
    // wait-for condition. Only the explicit 7th argument (rarely used) gates the callback.
    var multi = multiSelectors || [];
    var pageId = config.page_identifier_selector || null;
    var excludeFromTimeout = config.exclude_from_timeout || [];
    var timeout = config.selector_timeout || 5000;
    var insertOnlyOnce = !!config.insert_only_once;
    var cssId = css ? 'mg-css-' + hash(css) : null;
    var label = runVariantBlock._label || 'variant';

    var pageSeen = false, tornDown = false, busy = false, observer = null;
    var notBefore = 0;
    var hasTextSelectors = selectors.some(function (s) { return s && typeof s === 'object' && s.text; });

    function teardown(reason) {
      if (tornDown) return;
      tornDown = true;
      if (observer) observer.disconnect();
      log(label, 'teardown:', reason);
      if (cssId) removeCSS(cssId);
      if (typeof cleanupFn === 'function') { try { cleanupFn(); } catch (e) { warn(label, 'cleanup error', e); } }
    }

    function attempt(mutations) {
      if (busy || tornDown) return;
      busy = true;
      try {
        if (pageId) {
          var pg = document.querySelector(pageId);
          if (!pg) { if (pageSeen) teardown('page identifier "' + pageId + '" removed'); return; }
          pageSeen = true;
        }
        if (Date.now() < notBefore) return;
        if (flagSelector && document.querySelector(flagSelector)) return; // already applied
        var els = selectors.map(findElement);
        var multis = multi.map(function (s) { return Array.prototype.slice.call(document.querySelectorAll(s)); });
        var ok = els.every(function (e) { return e !== null; }) && multis.every(function (a) { return a.length > 0; });
        if (!ok) return;
        if (cssId) injectCSS(css, cssId);
        log(label, 'applying');
        var r = callback(mutations || [], els, multis, { status: 'ok' });
        if (r && typeof r.then === 'function') r.catch(function (e) { warn(label, 'callback error', e); });
        if (insertOnlyOnce && observer) observer.disconnect();
        if (flagSelector && !document.querySelector(flagSelector)) {
          notBefore = Date.now() + 15; // same back-off Moonshot uses when the flag never appears
          log(label, 'flag selector not found after insertion:', flagSelector);
        }
      } catch (e) {
        warn(label, 'error', e);
        notBefore = Date.now() + 15;
      } finally {
        busy = false;
      }
    }

    attempt([]);
    observer = new MutationObserver(function (m) { attempt(m); });
    observer.observe(document.documentElement || document, { childList: true, subtree: true, attributes: true, characterData: hasTextSelectors });

    setTimeout(function () {
      if (tornDown || !DEBUG) return;
      if (pageId && !pageSeen) { log(label, 'timeout: page identifier never found:', pageId); return; }
      selectors.forEach(function (s) {
        var key = typeof s === 'string' ? s : s.text;
        if (!findElement(s) && excludeFromTimeout.indexOf(key) === -1) log(label, 'timeout: selector never found:', key);
      });
      multi.forEach(function (s) {
        if (!document.querySelector(s) && excludeFromTimeout.indexOf(s) === -1) log(label, 'timeout: multi selector never found:', s);
      });
    }, timeout);

    return Promise.resolve();
  }

  // --- Registration ------------------------------------------------------------------------
  // MGExp.run(id, {platform: 'mobile'|'desktop', target: '<targets key>', name: '...'}, fn)
  function run(id, opts, fn) {
    var entry = { id: id, name: opts.name || id, platform: opts.platform, target: opts.target, state: 'pending', reason: '' };
    registry.push(entry);
    if (DISABLED) { entry.state = 'skipped'; entry.reason = '?mgexp=off in this tab'; log(id, 'skipped (mgexp=off)'); return; }
    if (opts.platform && opts.platform !== getPlatform()) { entry.state = 'skipped'; entry.reason = 'platform is ' + getPlatform(); log(id, 'skipped (platform)'); return; }
    var t = targets[opts.target];
    if (!t) { entry.state = 'error'; entry.reason = 'unknown target ' + opts.target; warn(id, 'unknown target', opts.target); return; }
    if (!urlMatches(t[0])) { entry.state = 'skipped'; entry.reason = 'URL does not match ' + t[2]; log(id, 'skipped (url)'); return; }

    var started = false;
    function start() {
      if (started) return; started = true;
      entry.state = 'running';
      log(id, 'running');
      runVariantBlock._label = id;
      try { fn(); } catch (e) { entry.state = 'error'; entry.reason = String(e); warn(id, 'error', e); }
    }
    // JS rule may need DOM (price, cart container): evaluate now, then keep re-checking briefly.
    var rule = t[1], deadline = Date.now() + 10000;
    (function check() {
      var ok = false;
      try { ok = !!rule(); } catch (e) { ok = false; }
      if (ok) return start();
      if (Date.now() > deadline || (document.readyState === 'complete' && Date.now() > deadline - 7000)) {
        entry.state = 'skipped'; entry.reason = 'rule not met: ' + t[2]; log(id, 'skipped (rule)', t[2]);
        return;
      }
      setTimeout(check, 250);
    })();
  }

  window.MGExp = {
    run: run,
    runVariantBlock: runVariantBlock,
    getPlatform: getPlatform,
    targets: targets,
    disabled: DISABLED,
    debug: DEBUG,
    status: function () {
      var rows = registry.map(function (e) { return { id: e.id, platform: e.platform, target: e.target, state: e.state, reason: e.reason }; });
      if (console.table) console.table(rows); else console.log(rows);
      return rows;
    }
  };
  log('runtime loaded; platform =', getPlatform(), DISABLED ? '(DISABLED via ?mgexp=off)' : '');
})();
